import { postForFile, postJson } from './files';
import axios from 'axios';
import { CATALOG_BASE_URL } from '../constants';

export { downloadBlob } from './files';
export type { FileResult } from './files';

// ── Supplier grouping ────────────────────────────────────────────────────────

export interface SupplierGroupingSummary {
  suppliers: number;
  rows: number;
}

export function groupSuppliers(file: File, outputFilename?: string) {
  const form = new FormData();
  form.append('input_file', file);
  if (outputFilename?.trim()) form.append('output_filename', outputFilename.trim());
  return postForFile<SupplierGroupingSummary>(
    '/supplier-grouping/group',
    form,
    'Grouped_by_supplier.xlsx',
    'Grouping failed'
  );
}

// ── Remittance reader ────────────────────────────────────────────────────────

export interface RemittanceReaderSummary {
  files: number;
  files_with_rows: number;
  rows: number;
  positive_rows: number;
  negative_rows: number;
  tables: number;
  notes: string[];
}

export interface RemittanceReaderResult {
  summary: RemittanceReaderSummary;
  positive: { blob: Blob; filename: string };
  negative: { blob: Blob; filename: string };
}

function base64ToBlob(data: string): Blob {
  const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

/** One extraction run that returns both workbooks (non-negative rows and negative rows). */
export async function readRemittances(files: File[], outputFilename?: string): Promise<RemittanceReaderResult> {
  const form = new FormData();
  files.forEach((f) => form.append('pdf_files', f));
  if (outputFilename?.trim()) form.append('output_filename', outputFilename.trim());
  const data = await postJson<{
    summary: RemittanceReaderSummary;
    files: Record<'positive' | 'negative', { filename: string; data: string }>;
  }>('/remittance-reader/read', form, 'Reading failed');
  return {
    summary: data.summary,
    positive: { blob: base64ToBlob(data.files.positive.data), filename: data.files.positive.filename },
    negative: { blob: base64ToBlob(data.files.negative.data), filename: data.files.negative.filename },
  };
}

// ── Zental remittances ───────────────────────────────────────────────────────

export interface RemittanceSummary {
  contacts: number;
}

export function processRemittances(file: File, outputFilename?: string) {
  const form = new FormData();
  form.append('input_file', file);
  if (outputFilename?.trim()) form.append('output_filename', outputFilename.trim());
  return postForFile<RemittanceSummary>(
    '/zental-remittances/process',
    form,
    'Zental_Payment_Summary.zip',
    'Processing failed'
  );
}

// ── Data append (Intel Fusion) ───────────────────────────────────────────────

export interface DataAppendOptions {
  sheetName: string | number;
  removeDuplicates: boolean;
  dropColAOnly: boolean;
  outputFilename?: string;
}

export interface DataAppendSummary {
  files_processed: number;
  rows_appended: number;
  empty_files: string[];
  file_row_counts: Record<string, number>;
}

export function combineFiles(files: File[], options: DataAppendOptions) {
  const form = new FormData();
  files.forEach((file) => form.append('input_files', file));
  form.append(
    'options',
    JSON.stringify({
      sheet_name: options.sheetName,
      remove_duplicates: options.removeDuplicates,
      drop_col_a_only: options.dropColAOnly,
      ...(options.outputFilename?.trim() ? { output_filename: options.outputFilename.trim() } : {}),
    })
  );
  return postForFile<DataAppendSummary>(
    '/data-append/combine',
    form,
    'combined_output.xlsx',
    'Combining files failed'
  );
}

// ── SumUp reconciliation ─────────────────────────────────────────────────────

export interface SumupMessage {
  level: 'success' | 'warning';
  text: string;
}

export interface SumupSummary {
  month: string;
  messages: SumupMessage[];
  log: string[];
  vat_checked: boolean;
  vat_mismatch_lines: number;
  vat_mismatch: Record<string, string | number | null>[];
  vat_not_in_list_lines: number;
  vat_not_in_list: Record<string, string | number | null>[];
  vat_duplicate_count: number;
  vat_duplicates: Record<string, string | number | null>[];
}

export function reconcileSumup(salesFiles: File[], txnFiles: File[], vatFile: File | null) {
  const form = new FormData();
  salesFiles.forEach((file) => form.append('sales_files', file));
  txnFiles.forEach((file) => form.append('txn_files', file));
  if (vatFile) form.append('vat_file', vatFile);
  return postForFile<SumupSummary>(
    '/sumup-recon/reconcile',
    form,
    'SumUp_Reconciliation.xlsx',
    'Reconciliation failed'
  );
}

// ── Fixed assets register ────────────────────────────────────────────────────

export interface FixedAssetsInspection {
  tables: number;
  rows: number;
  headings: string[];
  columns: { name: string; is_year: boolean }[];
  suggested: Record<string, string | null>;
  year_defaults: Record<string, string>;
}

export interface FixedAssetsSummary {
  tables: number;
  rows: number;
  columns: number;
}

export async function inspectFixedAssets(file: File): Promise<FixedAssetsInspection> {
  const form = new FormData();
  form.append('input_file', file);
  try {
    const response = await axios.post<FixedAssetsInspection>(
      `${CATALOG_BASE_URL}/fixed-assets/inspect`,
      form
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.detail ||
          (error.response ? `Could not read the workbook (status ${error.response.status})` : 'Could not reach the backend. Is it running?')
      );
    }
    throw error;
  }
}

export function buildFixedAssets(
  file: File,
  mapping: Record<string, string | null>,
  yearLabels: Record<string, string>
) {
  const form = new FormData();
  form.append('input_file', file);
  form.append('options', JSON.stringify({ mapping, year_labels: yearLabels }));
  return postForFile<FixedAssetsSummary>(
    '/fixed-assets/build',
    form,
    'Fixed_Assets_Register.xlsx',
    'Building the register failed'
  );
}

// ── Bank reconciliation ──────────────────────────────────────────────────────

export interface BankReconSummary {
  account: string;
  messages: { level: 'success' | 'warning'; text: string }[];
  figures: Record<string, number>;
}

export interface LedgerField {
  key: string;
  label: string;
  required: boolean;
  advanced?: boolean;
  hint?: string;
}

export interface LedgerInspection {
  header_row: number;
  columns: string[];
  samples: Record<string, string[]>;
  fields: LedgerField[];
  mapping: Record<string, string | null>;
}

export function inspectLedger(ledger: File): Promise<LedgerInspection> {
  const form = new FormData();
  form.append('ledger_file', ledger);
  return postJson<LedgerInspection>('/bank-recon/ledger-headers', form, 'Could not read the ledger');
}

export interface BankReconOptions {
  ledgerMapping: Record<string, string | null>;
  dateTolerance: number;
  booksClosing: number | null;
  account?: string;
}

export function reconcileBank(ledger: File, statement: File, options: BankReconOptions) {
  const form = new FormData();
  form.append('ledger_file', ledger);
  form.append('statement_file', statement);
  form.append(
    'options',
    JSON.stringify({
      ledger_mapping: options.ledgerMapping,
      date_tolerance: options.dateTolerance,
      books_closing: options.booksClosing,
      account: options.account?.trim() || null,
    })
  );
  return postForFile<BankReconSummary>(
    '/bank-recon/reconcile',
    form,
    'bank_reconciliation.xlsx',
    'Reconciliation failed'
  );
}

// ── Zental aged payables ─────────────────────────────────────────────────────

export interface AgedPayablesSummary {
  sheets: number;
  log: string[];
}

export function combineAgedPayables(files: File[]) {
  const form = new FormData();
  files.forEach((file) => form.append('input_files', file));
  return postForFile<AgedPayablesSummary>(
    '/zental-aged-payables/combine',
    form,
    'Zental_Aged_Payables_Combined.xlsx',
    'Combining failed'
  );
}


// ── Weekly sales report ──────────────────────────────────────────────────────

export interface WeeklySalesSummary {
  week_label: string;
  messages: { level?: string; text?: string }[];
  log: string[];
}

export function updateWeeklyMaster(master: File, input: File, accountTransactions: File | null) {
  const form = new FormData();
  form.append('master_file', master);
  form.append('input_file', input);
  if (accountTransactions) form.append('account_file', accountTransactions);
  return postForFile<WeeklySalesSummary>(
    '/weekly-sales-report/update',
    form,
    'Master.xlsx',
    'Updating the master workbook failed'
  );
}
